/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { DateTime } from 'luxon'
import { FiX } from 'react-icons/fi'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import ActiveAlertsCard from '@/components/ui/asset-detail/ActiveAlertsCard'
import { DetailSheet, type DetailSheetProps } from '@/components/ui/detail-sheet/DetailSheet'
import { useApi, useDashboard } from '@/hooks'
import AssetHistoryChart from '@/insights/assets/historyGraph/AssetHistoryChart'
import { load as loadAssetHistory } from '@/insights/assets/historyGraph/HistoryGraph'
import type { AssetHistory, TemperatureLimits } from '@/insights/assets/historyGraph/types'
import { getUserTimezone } from '@/insights/common/timezone'
import { type ApiService, isInsightStatus } from '@/types/dashboard-types'

import { useColdStorage } from './ColdStorageContext'
import { type Units, toAlertRow } from './format'
import { HumidityChart } from './HumidityChart'
import { sensorColors } from './humidityOptions'

const DEFAULT_HOURS = 24

/**
 * A location's temperature graph: every sensor on one chart, with door-open periods
 * shaded the way the trailer graph shades them (both come from the asset history the
 * "Fresh InStore Temperature Graph" insight draws) and the location's range as limit
 * lines. Under it, every sensor's humidity over the same window, then every active alert
 * on the location. Shown in the detail sheet over the floor plan.
 */
export const LocationDetail = ({ tableHeader }: Pick<DetailSheetProps, 'tableHeader'>) => {
  const { api } = useApi()
  const context = useColdStorage()
  const units = useDashboard().configuration.units as Units
  const asset = context.assets.find((a) => a.id === context.locationModalId)
  const show = !!asset && context.companyId != null
  const companyId = context.companyId
  const assetId = asset?.id

  const [data, setData] = useState<AssetHistory>()
  const [error, setError] = useState('')
  // The window the temperature graph is drawing, which the humidity graph follows.
  const [viewWindow, setViewWindow] = useState<HistoryWindow>()

  const fetchHistory = useCallback(
    async (window: HistoryWindow): Promise<AssetHistory | undefined> => {
      if (assetId == null || companyId == null) return undefined
      const response = await loadAssetHistory(api, {
        companyId,
        assetId: String(assetId),
        date: 'custom',
        // A zoneless wall-clock in the profile timezone, as the history graph insight sends.
        from: window.from.setZone(getUserTimezone()).toFormat("yyyy-MM-dd'T'HH:mm:ss"),
        periodHours: String(window.to.diff(window.from, 'hours').hours),
      })
      return isInsightStatus(response) ? undefined : response
    },
    [api, companyId, assetId]
  )

  const defaultWindow = useMemo<HistoryWindow>(() => {
    const to = DateTime.now().setZone(getUserTimezone()).startOf('hour').plus({ hours: 1 })
    return { from: to.minus({ hours: DEFAULT_HOURS }), to }
    // Keyed on the location so each one opens on a fresh window.
  }, [assetId])

  useEffect(() => {
    setData(undefined)
    setError('')
    setViewWindow(undefined)
    if (!show) return
    let cancelled = false
    fetchHistory(defaultWindow)
      .then((history) => {
        if (cancelled) return
        if (history) setData(history)
        else setError('Unable to load the temperature history.')
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load the temperature history.')
      })
    return () => {
      cancelled = true
    }
  }, [show, fetchHistory, defaultWindow])

  const reload = useCallback(
    async (_: ApiService, window: HistoryWindow) => {
      try {
        return await fetchHistory(window)
      } catch {
        return undefined
      }
    },
    [fetchHistory]
  )

  const temperatureLimits = useMemo<TemperatureLimits | undefined>(
    () => (asset?.threshold ? { lower: asset.threshold.lowCelsius, upper: asset.threshold.highCelsius } : undefined),
    [asset?.threshold]
  )

  const temperatureNames = useMemo(
    () =>
      Object.entries(data?.monitoringPoints ?? {})
        .filter(([, samples]) => samples.length > 0)
        .map(([name]) => name),
    [data]
  )
  const humiditySensors = useMemo(() => {
    const sensors = asset?.sensors.filter((s) => s.type === 'temperature') ?? []
    const colors = sensorColors(
      temperatureNames,
      sensors.map((s) => s.name)
    )
    return sensors.map((s) => ({ id: s.id, name: s.name, color: colors.get(s.name) ?? '' }))
  }, [asset?.sensors, temperatureNames])

  if (!show || !asset) return null

  return (
    <DetailSheet
      isOpen
      tableHeader={tableHeader}
      bodyKey={asset.id}
      header={
        <div className="flex justify-between w-full items-center gap-4 min-w-0">
          <span className="truncate font-semibold">{asset.name}</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-primary-content/80">Location</span>
            <CloseButton onClick={() => context.setLocationModalId(undefined)} />
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="location-detail">
        <div className="relative min-h-[400px]">
          {data ? (
            <AssetHistoryChart
              data={data}
              from={defaultWindow.from}
              periodHours={DEFAULT_HOURS}
              reload={reload}
              units={units}
              temperatureLimits={temperatureLimits}
              flattenZoneLegend
              hideGeofences
              onViewChange={setViewWindow}
            />
          ) : error ? (
            <Alert type="error" message={error} />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
              <p className="text-sm text-gray-600">Loading history…</p>
            </div>
          )}
        </div>
        {data && humiditySensors.length > 0 && <HumidityChart sensors={humiditySensors} window={viewWindow} />}
        <ActiveAlertsCard alerts={asset.activeAlerts.map(toAlertRow)} />
      </div>
    </DetailSheet>
  )
}

/** The sheet's close button, on its blue title bar. */
export const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button type="button" aria-label="Close details" className="btn btn-ghost btn-xs btn-circle" onClick={onClick}>
    <FiX className="w-4 h-4" />
  </button>
)
